/**
 * PATH: apps/qa/src/lib/api/sources.ts
 * FILE_ID: QA-SOURCE-API-001
 * PURPOSE: Typed source-document extraction and coverage contracts.
 */

import { api, normaliseArray, normaliseList } from './helpers'

export type SourceDocumentKind = 'standard' | 'supplier_manual' | 'specification' | 'other'
export type SourceRegistrationKind = 'standard' | 'manual' | 'tds' | 'ild' | 'consultant'
export type SourceDocumentState = 'pending' | 'processing' | 'extracted' | 'failed' | 'reviewed' | 'rejected'
export type SourceCoverageState = 'proposed' | 'accepted' | 'rejected' | 'dismissed' | 'gap' | 'conflict'

export interface SourceCitation {
  document_id: string
  document_title: string
  file_ref: string
  clause_id: string
  locator: string
}

export interface SuggestedTemplateItem {
  id: string
  item_number: string
  title: string
  product: string
  requirement_text: string
  acceptance_criteria: string
}

export interface SourceCoverage {
  id: string
  clause: string
  state: SourceCoverageState
  confidence: number
  rationale: string
  match_metadata: Record<string, unknown>
  suggested_item: SuggestedTemplateItem | null
  citations: SourceCitation[]
  template_item: string | null
  requirement_snapshot: string | null
  parent: string | null
  decided_at: string | null
}

export interface SourceClause {
  id: string
  locator: string
  heading: string
  clause_text: string
  content_hash: string
  category: string
  product_identity: string
  extraction_metadata: Record<string, unknown>
  decision: 'proposed' | 'accepted' | 'rejected'
  decided_at: string | null
  coverages: Array<{
    id: string
    state: SourceCoverageState
    confidence: number
    rationale: string
    match_metadata: Record<string, unknown>
    template_item_id: string | null
    requirement_snapshot_id: string | null
    parent_id: string | null
    decided_at: string | null
  }>
}

export interface SourceDocument {
  id: string
  organisation: string
  job: string | null
  file_record: string
  kind: SourceDocumentKind
  title: string
  source_metadata?: Record<string, unknown>
  state: SourceDocumentState
  extraction_metadata: {
    source?: string
    chunk_count?: number
    clause_count?: number
    compliance_decision?: 'not_assessed'
    status?: 'processing' | 'completed' | 'failed'
    failure?: string
    failure_code?: string
    retryable?: boolean
    scope?: DigestMapScope
    ai_runs?: number
  }
  reviewed_at: string | null
  updated_at: string
  source_filename: string
  source_display_name: string
  source_ref_code: string
  source_preview_url: string | null
  clauses: SourceClause[]
}

export interface QDriveFileRecord {
  id: string
  filename: string
  original_filename?: string
  content_type?: string | null
  file_size?: number
  preview_url?: string | null
  download_url?: string | null
  job_id?: string | number | null
}

export interface RegisterSourceDocumentRequest {
  file_record_id: string
  kind: SourceDocumentKind
  title: string
  job_id?: string | null
  source_metadata: {
    registration_kind: SourceRegistrationKind
    template_id: string
    filename?: string
  }
}

export interface DigestMapScope {
  page_numbers?: number[]
  section_names?: string[]
  chunk_ids?: string[]
  chunk_limit?: number
}

export interface DigestMapRequest {
  template_id: string
  scope: DigestMapScope
  use_ai: boolean
  use_embeddings: boolean
}

export interface DigestMapResponse {
  document_id: string
  template_id: string
  status: 'completed' | 'failed'
  execution: 'sync'
  retryable: boolean
  failure?: { code: string; detail: string }
  progress: {
    status?: 'processing' | 'completed' | 'failed'
    chunk_count?: number
    clause_count?: number
    scope?: DigestMapScope
    ai_runs?: number
    failure_code?: string
    failure?: string
  }
  clause_count?: number
  coverage_count?: number
  clauses: SourceClause[]
  coverage: SourceCoverage[]
}

export interface CoverageDecisionRequest {
  coverage_id: string
  action: 'accept' | 'edit' | 'dismiss'
  template_item_id?: string
  append_citations?: boolean
  create_item?: {
    section_id: string
    item_number?: string
    title?: string
    product?: string
    requirement_text?: string
    acceptance_criteria?: string
  }
  rationale?: string
}

export const sourceDocumentApi = {
  getDocuments: async (): Promise<SourceDocument[]> => {
    const response = await api().get('/qa/source-documents/')
    return normaliseArray<SourceDocument>(response.data)
  },
  getQDriveFiles: async (search = ''): Promise<QDriveFileRecord[]> => {
    const response = await api().get('/qdrive/files/', { params: { search, page_size: 50, ordering: '-uploaded_at' } })
    return normaliseList<QDriveFileRecord>(response.data).results
  },
  uploadQDriveFile: async (file: File, onProgress?: (percent: number) => void): Promise<QDriveFileRecord> => {
    const form = new FormData()
    form.append('file', file)
    const response = await api().post('/qdrive/files/upload/', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (event: { loaded: number; total?: number }) => {
        if (event.total) onProgress?.(Math.round((event.loaded / event.total) * 100))
      },
    })
    return response.data
  },
  registerDocument: async (data: RegisterSourceDocumentRequest): Promise<SourceDocument> => {
    const response = await api().post('/qa/source-documents/', data)
    return response.data
  },
  retryExtraction: async (id: string): Promise<{ document: string; clause_count: number }> => {
    const response = await api().post(`/qa/source-documents/${id}/retry-extraction/`)
    return response.data
  },
  digestMap: async (id: string, data: DigestMapRequest): Promise<DigestMapResponse> =>
    postDigestMap(`/qa/source-documents/${id}/digest-map/`, data),
  retryDigestMap: async (id: string, data: DigestMapRequest): Promise<DigestMapResponse> =>
    postDigestMap(`/qa/source-documents/${id}/retry-digest-map/`, data),
  getCoverage: async (id: string, templateId: string): Promise<SourceCoverage[]> => {
    const response = await api().get(`/qa/source-documents/${id}/coverage/`, { params: { template_id: templateId } })
    return normaliseArray<SourceCoverage>(response.data)
  },
  decideCoverage: async (id: string, data: CoverageDecisionRequest): Promise<SourceCoverage> => {
    const response = await api().post(`/qa/source-documents/${id}/decide-coverage/`, data)
    return response.data
  },
}

export function toSourceDocumentKind(kind: SourceRegistrationKind): SourceDocumentKind {
  if (kind === 'manual') return 'supplier_manual'
  if (kind === 'tds' || kind === 'ild') return 'specification'
  if (kind === 'consultant') return 'other'
  return 'standard'
}

async function postDigestMap(url: string, data: DigestMapRequest): Promise<DigestMapResponse> {
  try {
    const response = await api().post(url, data)
    return response.data
  } catch (error) {
    const failure = error && typeof error === 'object' && 'response' in error
      ? (error as { response?: { status?: number; data?: DigestMapResponse } }).response
      : undefined
    if (failure?.status === 422 && failure.data?.status === 'failed') return failure.data
    throw error
  }
}
